import { useState, useEffect } from 'react';

interface Props {
  onConfirm: () => void;
}

const AMBIENT_PARTICLES = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  left: `${5 + ((i * 41 + 7) % 90)}%`,
  size: 1.5 + (i % 3) * 0.8,
  delay: `${(i * 0.28) % 5}s`,
  duration: `${4 + (i % 5) * 1.2}s`,
  color: i % 3 === 0 ? '#d4a843' : i % 3 === 1 ? '#e8c060' : '#a07830',
  drift: `${((i % 7) - 3) * 18}px`,
}));

const GLOWS = [
  { left: '15%', top: '30%', size: 320, color: 'rgba(212,168,67,0.07)', delay: '0s', dur: '6s' },
  { left: '75%', top: '55%', size: 260, color: 'rgba(180,130,40,0.05)', delay: '2s', dur: '7s' },
  { left: '45%', top: '75%', size: 200, color: 'rgba(212,168,67,0.06)', delay: '1s', dur: '5s' },
];

const BURST = Array.from({ length: 56 }, (_, i) => {
  const angle = (i / 56) * 2 * Math.PI + (i % 3) * 0.15;
  const dist = 60 + (i % 7) * 28;
  const colors = ['#d4a843', '#f0d068', '#e8b840', '#fff3b0', '#c49030', '#ffe88a', '#ffffff', '#ffd000', '#ff6600', '#ffaa00'];
  return {
    id: i,
    dx: Math.cos(angle) * dist,
    dy: Math.sin(angle) * dist,
    size: 2 + (i % 5) * 1.6,
    color: colors[i % colors.length],
    delay: (i % 8) * 18,
    duration: 500 + (i % 5) * 120,
  };
});

// Electric arc positions around button
const ARCS = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  x: `${10 + (i * 12)}%`,
  delay: `${i * 0.18}s`,
  duration: `${0.8 + (i % 3) * 0.4}s`,
  side: i < 4 ? 'top' : 'bottom',
}));

const FONT_DECO = "'Cinzel', serif";
const FONT_HUD = "'Orbitron', monospace";

export default function StoryIntroModal({ onConfirm }: Props) {
  const [phase, setPhase] = useState(0);
  const [exploding, setExploding] = useState(false);
  const [flashVisible, setFlashVisible] = useState(false);
  const [blink, setBlink] = useState(true);
  const [powerLevel, setPowerLevel] = useState(0);
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 300);
    const t2 = setTimeout(() => setPhase(2), 1400);
    const t3 = setTimeout(() => setPhase(3), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  // Blink PRESS TO START
  useEffect(() => {
    if (exploding) return;
    const id = setInterval(() => setBlink(b => !b), 620);
    return () => clearInterval(id);
  }, [exploding]);

  // Power level charging animation (0 → 100 loop)
  useEffect(() => {
    if (exploding || phase < 3) return;
    let val = 0;
    const id = setInterval(() => {
      val = (val + 1.2) % 101;
      setPowerLevel(Math.round(val));
    }, 30);
    return () => clearInterval(id);
  }, [exploding, phase]);

  // Glitch effect every few seconds
  useEffect(() => {
    if (exploding || phase < 3) return;
    const trigger = () => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 180);
    };
    const id = setInterval(trigger, 2800);
    return () => clearInterval(id);
  }, [exploding, phase]);

  const handleClick = () => {
    if (exploding) return;
    setExploding(true);
    setFlashVisible(true);
    setTimeout(() => setFlashVisible(false), 350);
    setTimeout(() => onConfirm(), 900);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto"
      style={{ background: '#000' }}
    >
      {/* Screen flash overlay */}
      <div
        className="absolute inset-0 z-20 pointer-events-none transition-opacity duration-300"
        style={{
          opacity: flashVisible ? 1 : 0,
          background: 'radial-gradient(ellipse at center, rgba(212,168,67,0.6) 0%, rgba(255,100,0,0.2) 40%, transparent 70%)',
        }}
      />

      {/* Ambient glow orbs */}
      {GLOWS.map((g, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: g.left, top: g.top,
            width: g.size, height: g.size,
            transform: 'translate(-50%, -50%)',
            background: `radial-gradient(circle, ${g.color} 0%, transparent 70%)`,
            animation: `storyGlowPulse ${g.dur} ${g.delay} ease-in-out infinite`,
          }}
        />
      ))}

      {/* Floating ambient embers */}
      {AMBIENT_PARTICLES.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: p.left, bottom: '0',
            width: p.size, height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            '--drift': p.drift,
            animation: `storyEmber ${p.duration} ${p.delay} ease-in-out infinite`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}

      {/* Scanline */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 3px)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-md mx-auto px-5 sm:px-6 py-7 sm:py-12 flex flex-col items-center">

        {/* Opening quote */}
        <div
          className={`text-center mb-5 sm:mb-8 transition-all duration-1000 ${
            phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-4 sm:mb-5">
            <div className="h-px w-10 bg-gradient-to-r from-transparent to-[#d4a843]/50" />
            <div
              className="w-8 h-8 flex items-center justify-center rounded-full"
              style={{ border: '1px solid rgba(212,168,67,0.3)', background: 'rgba(212,168,67,0.05)' }}
            >
              <i className="ri-sword-fill text-[#d4a843]/60 text-xs" />
            </div>
            <div className="h-px w-10 bg-gradient-to-l from-transparent to-[#d4a843]/50" />
          </div>
          <p
            className="text-base sm:text-xl font-bold italic leading-loose"
            style={{ fontFamily: 'Georgia, serif', color: '#d4a843', textShadow: '0 0 25px rgba(212,168,67,0.45)' }}
          >
            時間沖淡了曾經的瘋狂，<br />
            但靈魂的共鳴從未消失。
          </p>
        </div>

        {/* Story */}
        <div
          className={`w-full transition-all duration-1000 ${
            phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-4 sm:mb-7">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#d4a843]/25" />
            <span className="text-[#d4a843]/40 text-[9px] tracking-[0.5em] uppercase">Soul Resonance</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#d4a843]/25" />
          </div>
          <div className="space-y-2.5 sm:space-y-3 text-center">
            <p className="text-[#c0c0c0] text-sm sm:text-base leading-relaxed">
              歡迎來到<strong className="text-[#d4a843]">靈魂共鳴賽</strong>。
            </p>
            <p className="text-[#888] text-sm leading-loose">
              今晚，請卸下大人的偽裝，<br />在螢幕前迎接命運的分組。
            </p>
            <p className="text-[#777] text-sm leading-loose">
              這不是一場普通的聚會，<br />
              而是一場<strong className="text-[#bbb]">為了喚醒友誼的生存戰</strong>。
            </p>
            <p className="text-[#666] text-sm leading-loose">
              黑隊與白隊，請準備好累積戰鬥積分。<br />
              最終的贏家，將奪得至高無上的榮耀，<br />
              並贏得「下一次旅程」的<strong className="text-[#bbb]">絕對主導權</strong>！
            </p>
            <p className="text-[#888] text-sm italic pt-1" style={{ fontFamily: 'Georgia, serif' }}>
              為隊伍而戰，為回憶奪分。
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4 sm:mt-7 mb-5 sm:mb-8">
            <div className="h-px flex-1 bg-[#d4a843]/15" />
            <div className="flex gap-1 items-center">
              <div className="w-1 h-1 rounded-full bg-[#d4a843]/30" />
              <div className="w-1.5 h-1.5 rounded-full bg-[#d4a843]/50" />
              <div className="w-1 h-1 rounded-full bg-[#d4a843]/30" />
            </div>
            <div className="h-px flex-1 bg-[#d4a843]/15" />
          </div>
        </div>

        {/* CTA Button */}
        <div
          className={`transition-all duration-700 flex flex-col items-center gap-3 w-full ${
            phase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="relative w-full max-w-xs" style={{ overflow: 'visible' }}>

            {/* Outer glow aura — dual layer */}
            {!exploding && (
              <div className="absolute -inset-4 rounded-3xl pointer-events-none ps-aura" />
            )}

            {/* Side energy bars */}
            {!exploding && (
              <>
                <div className="absolute -left-5 top-1/2 -translate-y-1/2 flex flex-col gap-1 pointer-events-none">
                  {[1,0.7,0.45,0.25].map((o, i) => (
                    <div key={i} className="ps-ebar-l rounded-full"
                      style={{ width: '3px', height: '10px', background: `rgba(212,168,67,${o})`, animationDelay: `${i * 0.12}s` }} />
                  ))}
                </div>
                <div className="absolute -right-5 top-1/2 -translate-y-1/2 flex flex-col gap-1 pointer-events-none">
                  {[0.25,0.45,0.7,1].map((o, i) => (
                    <div key={i} className="ps-ebar-r rounded-full"
                      style={{ width: '3px', height: '10px', background: `rgba(212,168,67,${o})`, animationDelay: `${i * 0.12}s` }} />
                  ))}
                </div>
              </>
            )}

            {/* Electric arcs top/bottom */}
            {!exploding && ARCS.map(arc => (
              <div key={arc.id}
                className="absolute pointer-events-none ps-arc"
                style={{
                  [arc.side]: '-6px',
                  left: arc.x,
                  width: '2px',
                  height: '6px',
                  background: 'linear-gradient(180deg, rgba(212,168,67,0.9), transparent)',
                  animationDelay: arc.delay,
                  animationDuration: arc.duration,
                }}
              />
            ))}

            {/* Burst shockwave rings on click */}
            {exploding && [0, 80, 160].map(delay => (
              <div key={delay}
                className="absolute rounded-full pointer-events-none"
                style={{
                  top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 10, height: 10,
                  border: delay === 0 ? '2px solid rgba(212,168,67,0.9)' : '1.5px solid rgba(255,200,50,0.6)',
                  animation: `shockwave ${0.65 + delay * 0.001}s ${delay}ms ease-out forwards`,
                }}
              />
            ))}

            {/* Burst particles */}
            {exploding && BURST.map(p => (
              <div
                key={p.id}
                className="absolute rounded-full pointer-events-none"
                style={{
                  top: '50%', left: '50%',
                  width: p.size, height: p.size,
                  marginTop: -(p.size / 2),
                  marginLeft: -(p.size / 2),
                  background: p.color,
                  boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
                  '--dx': `${p.dx}px`,
                  '--dy': `${p.dy}px`,
                  animation: `burstFly ${p.duration}ms ${p.delay}ms cubic-bezier(0.22,1,0.36,1) forwards`,
                } as React.CSSProperties}
              />
            ))}

            {/* === THE BUTTON === */}
            <button
              onClick={handleClick}
              disabled={exploding}
              className={`ps-btn relative overflow-hidden rounded-2xl cursor-pointer whitespace-nowrap active:scale-[0.94] transition-transform w-full ${exploding ? 'ps-btn-explode' : ''}`}
              style={{
                padding: '20px 36px',
                background: exploding
                  ? 'linear-gradient(135deg,#e8c055,#fff3a0,#e8c055)'
                  : 'linear-gradient(160deg,#200f00,#160900,#0e0600)',
                color: exploding ? '#0d0700' : '#f5c84a',
              }}
            >
              {/* Scanline overlay */}
              {!exploding && (
                <div
                  className="absolute inset-0 pointer-events-none z-0 opacity-[0.04]"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(212,168,67,0.9) 2px,rgba(212,168,67,0.9) 3px)',
                    backgroundSize: '100% 3px',
                  }}
                />
              )}

              {/* Sweep light */}
              {!exploding && (
                <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                  <div
                    className="ps-sweep absolute top-0 bottom-0 w-1/3"
                    style={{ background: 'linear-gradient(90deg,transparent,rgba(212,168,67,0.18),transparent)' }}
                  />
                </div>
              )}

              {/* Corner brackets — larger, bolder */}
              {!exploding && (
                <>
                  <div className="absolute top-3 left-3 w-5 h-5 pointer-events-none" style={{ borderTop: '2px solid rgba(212,168,67,1)', borderLeft: '2px solid rgba(212,168,67,1)' }} />
                  <div className="absolute top-3 right-3 w-5 h-5 pointer-events-none" style={{ borderTop: '2px solid rgba(212,168,67,1)', borderRight: '2px solid rgba(212,168,67,1)' }} />
                  <div className="absolute bottom-3 left-3 w-5 h-5 pointer-events-none" style={{ borderBottom: '2px solid rgba(212,168,67,1)', borderLeft: '2px solid rgba(212,168,67,1)' }} />
                  <div className="absolute bottom-3 right-3 w-5 h-5 pointer-events-none" style={{ borderBottom: '2px solid rgba(212,168,67,1)', borderRight: '2px solid rgba(212,168,67,1)' }} />
                </>
              )}

              {/* Corner sparks */}
              {!exploding && [
                'top-[11px] left-[11px]',
                'top-[11px] right-[11px]',
                'bottom-[11px] left-[11px]',
                'bottom-[11px] right-[11px]',
              ].map((pos, i) => (
                <div
                  key={i}
                  className={`ps-spark absolute ${pos} w-1.5 h-1.5 rounded-full pointer-events-none`}
                  style={{ background: '#d4a843', animationDelay: `${i * 0.22}s` }}
                />
              ))}

              {/* Content */}
              <div className="relative z-10 flex flex-col items-center gap-2">
                {/* Sword + title row */}
                <div className="flex items-center gap-4">
                  {!exploding && <i className="ps-sword-l ri-sword-fill text-2xl" style={{ color: '#d4a843' }} />}
                  {exploding && <i className="ri-flashlight-fill text-2xl text-[#0d0700]" />}

                  <div className="flex flex-col items-center gap-1">
                    {/* HUD tag above title */}
                    {!exploding && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-[#e05555] animate-pulse" />
                        <span style={{ fontFamily: FONT_HUD, fontSize: '7px', color: 'rgba(224,85,85,0.8)', letterSpacing: '0.3em' }}>BATTLE MODE</span>
                        <div className="w-2 h-2 rounded-full bg-[#e05555] animate-pulse" />
                      </div>
                    )}
                    <span
                      className={`font-black tracking-[0.2em] ${exploding ? 'ps-title-explode' : glitch ? 'ps-glitch' : 'ps-title'}`}
                      style={{
                        fontFamily: exploding ? "'Cinzel', serif" : FONT_DECO,
                        fontSize: '20px',
                        color: exploding ? '#0d0700' : undefined,
                        textShadow: exploding ? 'none' : undefined,
                      }}
                    >
                      {exploding ? '⚡ 衝入戰場！' : '⚔ 進入戰場'}
                    </span>
                  </div>

                  {!exploding && <i className="ps-sword-r ri-sword-fill text-2xl" style={{ color: '#d4a843' }} />}
                  {exploding && <i className="ri-flashlight-fill text-2xl text-[#0d0700]" />}
                </div>

                {/* Power bar */}
                {!exploding && (
                  <div className="flex items-center gap-2 w-full px-2">
                    <span style={{ fontFamily: FONT_HUD, fontSize: '7px', color: 'rgba(212,168,67,0.5)', letterSpacing: '0.2em' }}>PWR</span>
                    <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.2)' }}>
                      <div
                        className="h-full rounded-full transition-none"
                        style={{
                          width: `${powerLevel}%`,
                          background: powerLevel > 70
                            ? 'linear-gradient(90deg,#d4a843,#fff380,#d4a843)'
                            : 'linear-gradient(90deg,#a06010,#d4a843)',
                          boxShadow: powerLevel > 70 ? '0 0 6px rgba(212,168,67,0.8)' : 'none',
                          transition: 'width 0.05s linear',
                        }}
                      />
                    </div>
                    <span style={{ fontFamily: FONT_HUD, fontSize: '7px', color: powerLevel > 70 ? 'rgba(212,168,67,0.9)' : 'rgba(212,168,67,0.4)', letterSpacing: '0.1em' }}>{powerLevel}%</span>
                  </div>
                )}
              </div>
            </button>

            {/* Power level bar BELOW button (external) */}
            {!exploding && (
              <div className="mt-1.5 flex items-center justify-center gap-1">
                {Array.from({ length: 12 }, (_, i) => (
                  <div key={i}
                    className="rounded-sm transition-all duration-75"
                    style={{
                      width: '6px',
                      height: i < 4 ? '8px' : i < 8 ? '11px' : '14px',
                      background: (i / 12) * 100 < powerLevel
                        ? `rgba(212,168,67,${0.4 + (i / 12) * 0.6})`
                        : 'rgba(212,168,67,0.08)',
                      boxShadow: (i / 12) * 100 < powerLevel && powerLevel > 70
                        ? '0 0 4px rgba(212,168,67,0.8)' : 'none',
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Blinking PRESS TO START / status */}
          <div className="flex flex-col items-center gap-1">
            {!exploding ? (
              <p
                className="tracking-[0.5em] uppercase font-black transition-opacity duration-75"
                style={{
                  fontFamily: FONT_HUD,
                  fontSize: '8px',
                  color: 'rgba(212,168,67,0.85)',
                  opacity: blink ? 1 : 0,
                  letterSpacing: '0.48em',
                }}
              >
                ◆ PRESS TO START ◆
              </p>
            ) : (
              <p
                className="text-[10px] tracking-[0.3em] uppercase"
                style={{ fontFamily: FONT_HUD, color: 'rgba(212,168,67,0.6)' }}
              >
                進入戰場中…
              </p>
            )}
            {!exploding && (
              <p className="text-[#2a2a2a] text-[10px] text-center tracking-wider">
                點擊後進入隊伍選擇
              </p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* ── Ambient ── */
        @keyframes storyEmber {
          0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
          15%  { opacity: 0.6; }
          85%  { opacity: 0.4; }
          100% { opacity: 0; transform: translateY(-65vh) translateX(var(--drift)) scale(0.3); }
        }
        @keyframes storyGlowPulse {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(0.85); }
          50%       { opacity: 1;   transform: translate(-50%, -50%) scale(1.15); }
        }

        /* ── Click burst ── */
        @keyframes burstFly {
          0%   { opacity: 1; transform: translate(0, 0) scale(1); }
          70%  { opacity: 0.7; }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.2); }
        }
        @keyframes shockwave {
          0%   { transform: translate(-50%,-50%) scale(1);  opacity: 1; }
          100% { transform: translate(-50%,-50%) scale(22); opacity: 0; }
        }

        /* ── Button pulse ── */
        .ps-btn {
          border: 2px solid rgba(212,168,67,0.6);
          animation: psBtnPulse 1.8s ease-in-out infinite;
        }
        @keyframes psBtnPulse {
          0%, 100% {
            box-shadow: 0 0 24px rgba(212,168,67,0.3), 0 0 50px rgba(212,168,67,0.08), inset 0 0 20px rgba(212,168,67,0.04);
            border-color: rgba(212,168,67,0.5);
          }
          50% {
            box-shadow: 0 0 60px rgba(212,168,67,0.75), 0 0 120px rgba(212,168,67,0.25), inset 0 0 45px rgba(212,168,67,0.09);
            border-color: rgba(212,168,67,1);
          }
        }
        .ps-btn-explode {
          animation: none;
          box-shadow: 0 0 100px rgba(212,168,67,1), 0 0 200px rgba(212,168,67,0.6) !important;
          border-color: rgba(255,220,80,0.9) !important;
        }

        /* ── Outer aura ── */
        .ps-aura {
          animation: psAuraPulse 1.8s ease-in-out infinite;
        }
        @keyframes psAuraPulse {
          0%, 100% { box-shadow: 0 0 0px rgba(212,168,67,0); background: transparent; }
          50%       { box-shadow: 0 0 40px rgba(212,168,67,0.2), 0 0 80px rgba(212,168,67,0.08); background: transparent; }
        }

        /* ── Title glow ── */
        .ps-title {
          animation: psTitleGlow 1.4s ease-in-out infinite;
        }
        @keyframes psTitleGlow {
          0%, 100% { color: #e8b840; text-shadow: 0 0 14px rgba(212,168,67,0.6), 0 0 30px rgba(212,168,67,0.2); }
          50%       { color: #fff380; text-shadow: 0 0 35px rgba(212,168,67,1), 0 0 70px rgba(212,168,67,0.5), 0 0 3px #fff; }
        }
        .ps-title-explode {
          animation: psTitleFlash 0.3s ease-in-out 2;
        }
        @keyframes psTitleFlash {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }

        /* ── Glitch ── */
        .ps-glitch {
          animation: psGlitch 0.18s steps(2) forwards;
          color: #fff380;
        }
        @keyframes psGlitch {
          0%   { text-shadow: 2px 0 rgba(255,0,80,0.8), -2px 0 rgba(0,200,255,0.8); transform: translateX(0); }
          25%  { text-shadow: -3px 1px rgba(255,0,80,0.9), 3px -1px rgba(0,200,255,0.9); transform: translateX(2px); }
          50%  { text-shadow: 2px -1px rgba(255,0,80,0.8), -2px 1px rgba(0,200,255,0.8); transform: translateX(-2px); }
          75%  { text-shadow: -2px 0 rgba(255,0,80,0.9), 2px 0 rgba(0,200,255,0.9); transform: translateX(1px); }
          100% { text-shadow: 0 0 14px rgba(212,168,67,0.6); transform: translateX(0); color: #e8b840; }
        }

        /* ── Sweep ── */
        .ps-sweep {
          left: -100%;
          animation: psSweep 2.4s ease-in-out infinite;
        }
        @keyframes psSweep {
          0%   { left: -100%; opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { left: 200%; opacity: 0; }
        }

        /* ── Swords ── */
        .ps-sword-l {
          animation: psSwordL 1.8s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes psSwordL {
          0%, 100% { opacity: 0.3; transform: rotate(-40deg) scale(0.85); filter: none; }
          50%       { opacity: 1;   transform: rotate(-62deg) scale(1.25) translateX(-4px); filter: drop-shadow(0 0 10px rgba(212,168,67,1)); }
        }
        .ps-sword-r {
          animation: psSwordR 1.8s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes psSwordR {
          0%, 100% { opacity: 0.3; transform: scaleX(-1) rotate(-40deg) scale(0.85); filter: none; }
          50%       { opacity: 1;   transform: scaleX(-1) rotate(-62deg) scale(1.25) translateX(-4px); filter: drop-shadow(0 0 10px rgba(212,168,67,1)); }
        }

        /* ── Corner sparks ── */
        .ps-spark {
          animation: psSpark 1.1s ease-in-out infinite;
        }
        @keyframes psSpark {
          0%, 100% { opacity: 0.15; transform: scale(0.5); box-shadow: none; }
          50%       { opacity: 1;    transform: scale(2); box-shadow: 0 0 10px rgba(212,168,67,1), 0 0 20px rgba(212,168,67,0.5); }
        }

        /* ── Electric arcs ── */
        .ps-arc {
          animation: psArc 0.8s ease-in-out infinite;
          transform-origin: top center;
        }
        @keyframes psArc {
          0%, 100% { opacity: 0; transform: scaleY(0); }
          30%, 70% { opacity: 0; }
          50%       { opacity: 1; transform: scaleY(1); }
        }

        /* ── Side energy bars ── */
        .ps-ebar-l {
          animation: psEbarL 1.8s ease-in-out infinite;
        }
        @keyframes psEbarL {
          0%, 100% { opacity: 0.2; transform: scaleY(0.6); }
          50%       { opacity: 1;   transform: scaleY(1.2); box-shadow: 0 0 6px rgba(212,168,67,0.8); }
        }
        .ps-ebar-r {
          animation: psEbarR 1.8s ease-in-out infinite;
        }
        @keyframes psEbarR {
          0%, 100% { opacity: 0.2; transform: scaleY(0.6); }
          50%       { opacity: 1;   transform: scaleY(1.2); box-shadow: 0 0 6px rgba(212,168,67,0.8); }
        }
      `}</style>
    </div>
  );
}
